export const SPECIALIZATION_LABELS = {
  'pomocni radnik': 'Pomoćni radnik',
  junior: 'Junior',
  medior: 'Medior',
  senior: 'Senior',
  leader: 'Leader'
};

export const SPECIALIZATION_COEFFICIENTS = {
  'pomocni radnik': 1,
  junior: 2,
  medior: 3,
  senior: 4,
  leader: 5
};

const getRank = (specialization) => {
  const levels = ['pomocni radnik', 'junior', 'medior', 'senior', 'leader'];
  const index = levels.indexOf(specialization);
  return index >= 0 ? index + 1 : 1;
};

const canonicalPair = (levelA, levelB) => {
  if (getRank(levelA) >= getRank(levelB)) {
    return { higher: levelA, lower: levelB };
  }
  return { higher: levelB, lower: levelA };
};

const getCoef = (specialization) =>
  SPECIALIZATION_COEFFICIENTS[specialization] || 1;

const findRule = (rules, levelA, levelB) => {
  const { higher, lower } = canonicalPair(levelA, levelB);
  return rules.find(
    (rule) => rule.higherLevel === higher && rule.lowerLevel === lower
  );
};

const getPairSharesFromRule = (rule, levelA, levelB) => {
  const { higher } = canonicalPair(levelA, levelB);
  if (levelA === higher) {
    return { shareA: rule.higherSharePercent, shareB: rule.lowerSharePercent };
  }
  return { shareA: rule.lowerSharePercent, shareB: rule.higherSharePercent };
};

export const computeCoefficientShares = (workers) => {
  const totalCoef = workers.reduce(
    (sum, w) => sum + getCoef(w.specialization),
    0
  );

  if (totalCoef === 0) {
    const equal = Math.round((100 / workers.length) * 100) / 100;
    return workers.map((w) => ({
      workerId: w._id,
      sharePercent: equal,
      coefficient: getCoef(w.specialization),
      specialization: w.specialization
    }));
  }

  const raw = workers.map((w) => ({
    workerId: w._id,
    rawShare: (getCoef(w.specialization) / totalCoef) * 100,
    coefficient: getCoef(w.specialization),
    specialization: w.specialization
  }));

  const rounded = raw.map((entry) => ({
    ...entry,
    sharePercent: Math.round(entry.rawShare * 100) / 100
  }));

  const sum = rounded.reduce((acc, e) => acc + e.sharePercent, 0);
  const diff = Math.round((100 - sum) * 100) / 100;
  if (diff !== 0 && rounded.length > 0) {
    const highest = rounded.reduce((best, cur) =>
      cur.rawShare > best.rawShare ? cur : best
    );
    highest.sharePercent = Math.round((highest.sharePercent + diff) * 100) / 100;
  }

  return rounded.map(({ workerId, sharePercent, coefficient, specialization }) => ({
    workerId,
    sharePercent,
    coefficient,
    specialization
  }));
};

export const computePairShares = (workerA, workerB, rules) => {
  const rule = findRule(rules, workerA.specialization, workerB.specialization);
  if (rule) {
    const { shareA, shareB } = getPairSharesFromRule(
      rule,
      workerA.specialization,
      workerB.specialization
    );
    return [
      {
        workerId: workerA._id,
        sharePercent: shareA,
        coefficient: getCoef(workerA.specialization),
        specialization: workerA.specialization
      },
      {
        workerId: workerB._id,
        sharePercent: shareB,
        coefficient: getCoef(workerB.specialization),
        specialization: workerB.specialization
      }
    ];
  }
  return computeCoefficientShares([workerA, workerB]);
};

export const computeTeamSharesPreview = (workers, rules = []) => {
  if (!workers || workers.length === 0) return [];

  if (workers.length === 1) {
    const worker = workers[0];
    return [{
      workerId: worker._id,
      sharePercent: 100,
      coefficient: getCoef(worker.specialization),
      specialization: worker.specialization
    }];
  }

  if (workers.length === 2) {
    return computePairShares(workers[0], workers[1], rules);
  }

  return computeCoefficientShares(workers);
};

export const formatShareLabel = (specialization) =>
  SPECIALIZATION_LABELS[specialization] || specialization;
